import { isEqual } from "lodash";
import type { FC } from "react";
import { Fragment, useMemo } from "react";
import { ExtraStateContext } from "../..";
import { UserSelect } from "../../../../../../../components/input/select/UserSelect";
import { approverType } from "../../../../../../../constants/itCodeRequestConstants";
import { useSelector } from "../../../../../../../store/store";
import InformationList, { LeftRightContent } from "../../../../../components/InformationList";
import { ApprovalHistory } from "../../../../../components/Progress/molecule/ApprovalHistory";
import TableModal from "../../../../../components/detail";
import CommentSection from "../../../../../info-technology-process/commentsection/comment-sections";
import { useProcess } from "../../../../../process-context";
import { FileAttachmentCard } from "../../../../../upload";
import { eApprovedESCCOptions } from "../../../../companyRequestContants/companyRequestContants";
import { useLongTermTableColumns } from "../table-column";
import useApprovalProcess from "../../../../securityCardUnits/hooks/use-approval-prcoess";

const LongTermContent: FC = () => {
  const {
    Request,
    approvalStep,
    approvalType,
    comment,
    setComment,
    isProvide,
    isEvict,
    pickedItHead,
    onApproverChange,
  } = useProcess<ExtraStateContext>();

  const tableColumns = useLongTermTableColumns();

  const { fields } = Request;

  const { canApproval } = useApprovalProcess({
    data: Request as any,
    steps: [
      {
        approver: 'eApprovedBy',
        status: 'eApprovedStatus'
      },
      {
        approver: 'eApprovedByITLead',
        status: 'eApprovedByITLeadStatus'
      },
      {
        approver: 'eApprovedByITHead',
        status: 'eApprovedByITHeadStatus'
      },
      {
        approver: 'eAssignee',
        status: 'eAssigneeStatus'
      },
    ]
  })

  /**
   * Selector
   */
  const user = useSelector((state) => state.user);

  const { eDeptHeadComment, eITLeadComment, eITHeadComment, eAssigneeComment, eApprovedBy, eApprovedByITLead, eAssignee } = fields;

  const smColData = useMemo(() => {
    return [
      {
        content: (
          <LeftRightContent
            left="Người yêu cầu:"
            right={<strong>{Request.createdBy.user.displayName}</strong>}
            smColumn={false}
          />
        ),
      },
      {
        content: (
          <LeftRightContent
            left="SĐT Người yêu cầu:"
            right={<strong>{Request.fields.ePhoneNumber}</strong>}
            smColumn={false}
          />
        ),
      },
      {
        content: <LeftRightContent left="Cơ quan/Đơn vị:" right={<strong>{Request.fields.eDepartment}</strong>} />,
      },
      {
        hidden: {
          sm: isEqual(Request.fields.eUnit, Request.fields.eDepartment),
        },
        content: <LeftRightContent left="Đơn vị:" right={<strong>{Request.fields.eUnit}</strong>} />,
      },
      {
        content: <LeftRightContent left="Căn cứ theo nhu cầu:" right={<strong>{Request.fields.eBaseId}</strong>} />,
      },
      {
        content: <LeftRightContent left="Mục đích:" right={<strong>{Request.fields.ePurpose}</strong>} />,
      },
      // {
      //   content: (
      //     <LeftRightContent
      //       left="Công Văn Có Chữ Ký Của Lãnh Đạo Phòng:"
      //       right={
      //         <div className="d-flex align-items-center">
      //           <FileAttachmentCard
      //             listFile={
      //               Request.fields.eDocumentary ? JSON.parse(Request.fields.eDocumentary) : []
      //             }
      //           />
      //         </div>
      //       }
      //     />
      //   ),
      // },
      {
        content: (
          <LeftRightContent
            left="Chứng Chỉ Kiến Thức Chung Về An Ninh Hàng Không:"
            right={
              <div className="d-flex align-items-center">
                <FileAttachmentCard
                  listFile={
                    Request.fields.eAviationSecurityCertification
                      ? JSON.parse(Request.fields.eAviationSecurityCertification)
                      : []
                  }
                />
              </div>
            }
          />
        ),
      },
      {
        content: (
          <LeftRightContent
            left="Văn bản đính kèm:"
            right={
              <div className="d-flex align-items-center">
                <FileAttachmentCard
                  listFile={Request.fields.eDocumentAttach ? JSON.parse(Request.fields.eDocumentAttach) : []}
                />
              </div>
            }
          />
        ),
      },
      {
        content: (
          <LeftRightContent
            left="Loại thẻ:"
            right={<strong>{isProvide ? "Cấp mới" : isEvict ? "Gia hạn" : "Cấp lại"}</strong>}
            smColumn={false}
          />
        ),
      },
    ];
  }, [Request, isProvide, isEvict]);

  const fullColData = useMemo(() => {
    const eDeptHeadCommentData = eDeptHeadComment ? JSON.parse(eDeptHeadComment) : [];
    const eITLeadCommentData = eITLeadComment ? JSON.parse(eITLeadComment) : [];
    const eITHeadCommentData = eITHeadComment ? JSON.parse(eITHeadComment) : [];
    const eAssigneeCommentData = eAssigneeComment ? JSON.parse(eAssigneeComment) : [];

    return [
      {
        content: <TableModal data={JSON.parse(Request.fields.eContent)} columns={tableColumns} />,
      },
      {
        content: (
          <Fragment>
            Tệp đính kèm
            <div className="mt-2 d-flex flex-column">
              <FileAttachmentCard
                listFile={Request.fields.eFileContent ? JSON.parse(Request.fields.eFileContent) : []}
              />
            </div>
          </Fragment>
        ),
      },
      {
        content: (
          <Fragment>
            Lịch sử phê duyệt
            <ApprovalHistory>
              <ApprovalHistory.Content
                hidden={!eDeptHeadComment}
                data={eDeptHeadCommentData}
                highlight={`Thủ trưởng CQ/ĐV, ${eApprovedBy.split("|")[1]}`}
              />

              <ApprovalHistory.Content
                hidden={!eITLeadComment}
                data={eITLeadCommentData}
                highlight={`Bộ phận cấp thẻ VPCT, ${eApprovedByITLead.split("|")[1]}`}
              />

              <ApprovalHistory.Content
                hidden={!eITHeadComment}
                data={eITHeadCommentData}
                highlight={`Đã gửi công văn sang bộ công an, ${eApprovedByITLead.split("|")[1]}`}
              />

              <ApprovalHistory.Content
                hidden={!eAssigneeComment}
                data={eAssigneeCommentData}
                highlight={`Bộ phận thực hiện cấp thẻ VPCT, ${eAssignee.split("|")[1]}`}
              />

              <ApprovalHistory.Input
                hidden={!canApproval}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </ApprovalHistory>
          </Fragment>
        ),
      },
      {
        content: (
          <Fragment>
            Thông tin trao đổi
            <div className="mt-2">
              <CommentSection comments={[]} data={Request} user={user} id={8} className="mb-4" />
            </div>
          </Fragment>
        ),
      },
      {
        hidden: approvalType !== approverType.itlead || approvalStep !== approvalType,
        content: (
          <Fragment>
            Thực hiện cấp thẻ
            <div className="people_picker-custom-detail">
              <UserSelect
                name="Vui lòng chọn bộ phận hành chính VPCT"
                onTouched={() => 1}
                placeholder="Vui lòng chọn bộ phận Thực hiện cấp thẻ"
                options={eApprovedESCCOptions}
                value={pickedItHead}
                onChange={onApproverChange}
              />
            </div>
          </Fragment>
        ),
      }
    ];
  }, [eDeptHeadComment, eITLeadComment, eITHeadComment, eAssigneeComment, Request, tableColumns, eApprovedBy, eApprovedByITLead, eAssignee, canApproval, comment, user, approvalType, approvalStep, pickedItHead, onApproverChange, setComment]);

  return (
    <Fragment>
      <InformationList
        size="sm-col"
        data={smColData}
        renderContent={(item) => item.content}
        getItemHidden={(item) => item.hidden}
      />
      <InformationList
        size="full-col"
        data={fullColData}
        renderContent={(item) => item.content}
        getItemHidden={(item) => item.hidden}
      />
    </Fragment>
  );
};

export default LongTermContent;
